import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestYieldCallback } from "@theme/utilities";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("change", this.variantChanged.bind(this));
    this.updateStockBadges();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      }),
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      loadsNewProduct,
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest("quick-add-component") ||
      this.closest("swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`,
        )?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(
                JSON.parse(textContent),
                this.selectedOptionId,
                {
                  html,
                  productId: this.dataset.productId ?? "",
                  newProduct,
                },
              ),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Updates the stock badges visibility based on the inventory.
   * @param {HTMLElement} [root] - The root element to search for badges.
   */
  async updateStockBadges(root = this) {
    const token = this.dataset.accessToken;
    if (!token) return;

    const badges = Array.from(root.querySelectorAll(".variant-stock-badge"));
    if (badges.length === 0) return;

    const productGid = `gid://shopify/Product/${this.dataset.productId}`;
    const localeLanguageRaw = document.documentElement.lang || "en";
    const resolvedLanguageCode = ((id) => {
      if (!id) return "EN";
      const parts = id.split("-");
      const base = parts[0] ? parts[0].trim().toUpperCase() : "EN";
      return /^[A-Z]{2}$/.test(base) ? base : "EN";
    })(localeLanguageRaw);

    // Collect handles and variant IDs
    const variantIds = new Set();
    const handles = new Set();
    badges.forEach((badge) => {
      const b = /** @type {HTMLElement} */ (badge);
      if (b.dataset.variantId)
        variantIds.add("gid://shopify/ProductVariant/" + b.dataset.variantId);
      if (b.dataset.productUrl) {
        const handle = b.dataset.productUrl.split("/").pop()?.split("?")[0];
        if (handle) handles.add(handle);
      }
      if (b.dataset.handle) {
        handles.add(b.dataset.handle);
      }
    });

    const handlesArray = Array.from(handles);
    const productQueries = handlesArray
      .map((handle, i) => {
        return `p${i}: productByHandle(handle: ${JSON.stringify(
          handle,
        )}) { handle variants(first: 250) { edges { node { quantityAvailable selectedOptions { name value } } } } }`;
      })
      .join("\n");

    const query = `
      query ($id: ID!) @inContext(language: ${resolvedLanguageCode}) {
        mainProduct: product(id: $id) {
          handle
          variants(first: 250) {
            edges {
              node {
                id
                quantityAvailable
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
        }
        ${productQueries}
      }
    `;

    try {
      const response = await fetch("/api/2024-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables: { id: productGid } }),
      });
      const data = await response.json();
      if (!data || !data.data) return;

      /** @typedef { { id: string, quantityAvailable: number, selectedOptions: { name: string, value: string }[] } } VariantData */
      /** @type {Record<string, VariantData[]>} */
      const productVariantsMap = {}; // key: GID or handle, value: variant array

      // Main product variants
      if (data.data.mainProduct) {
        const variants = data.data.mainProduct.variants.edges.map((/** @type {any} */ e) => e.node);
        productVariantsMap[productGid] = variants;
        if (data.data.mainProduct.handle) {
          productVariantsMap[data.data.mainProduct.handle] = variants;
        }
      }

      // Other products
      Object.keys(data.data).forEach((k) => {
        if (k === "mainProduct") return;
        const p = data.data[k];
        if (!p) return;
        productVariantsMap[p.handle] = p.variants.edges.map((/** @type {any} */ e) => e.node);
      });

      // Get current selections for ALL options of the current picker
      /** @type {Record<string, { value: string, name: string }>} */
      const currentSelections = {}; // key: position (1-based), value: { value, name }
      this.querySelectorAll("select, fieldset").forEach((el) => {
        const element = /** @type {HTMLElement} */ (el);
        const select = element.querySelector("select");
        if (select) {
          const selectedOption = select.options[select.selectedIndex];
          if (selectedOption && selectedOption.dataset.optionPosition) {
            currentSelections[selectedOption.dataset.optionPosition] = {
              value: select.value,
              name: selectedOption.dataset.optionName || ""
            };
          }
        } else {
          const checked = element.querySelector("input:checked");
          if (checked && /** @type {HTMLElement} */ (checked).dataset.optionPosition) {
            const input = /** @type {HTMLInputElement} */ (checked);
            currentSelections[input.dataset.optionPosition || ""] = {
              value: input.value,
              name: input.dataset.optionName || ""
            };
          }
        }
      });

      // Update badges
      badges.forEach((badge) => {
        const b = /** @type {HTMLElement} */ (badge);
        let inStock = false;

        const badgePos = b.dataset.optionPosition;
        const badgeVal = b.dataset.optionValue;

        if (b.dataset.productUrl) {
          // Cross-product (swatches in product-colors)
          const handle = b.dataset.productUrl.split("/").pop()?.split("?")[0];
          const variants = handle ? productVariantsMap[handle] : null;
          inStock = variants ? variants.some((v) => v.quantityAvailable > 0) : false;
        } else if (badgePos && badgeVal) {
          // Local picker option
          const variants = productVariantsMap[productGid];
          if (variants) {
            const isColor = b.dataset.isColor === "true";

            if (isColor) {
              // Colors show total product stock (any variant in stock)
              inStock = variants.some((v) => v.quantityAvailable > 0);
            } else {
              // Other options (Size, etc.) show limited availability based on OTHER selections
              inStock = variants.some((v) => {
                if (v.quantityAvailable <= 0) return false;
                
                // Match this option's value precisely
                const matchesThis = v.selectedOptions.some((opt) => 
                  String(opt.value) === String(badgeVal)
                );
                if (!matchesThis) return false;

                // Match ALL OTHER currently selected options
                return Object.keys(currentSelections).every((pos) => {
                  if (pos === badgePos) return true; // Skip current option
                  const selection = currentSelections[pos];
                  if (!selection) return true;
                  
                  // Match both name (if available) and value
                  return v.selectedOptions.some((opt) => {
                    if (selection.name && opt.name !== selection.name) return false;
                    return String(opt.value) === String(selection.value);
                  });
                });
              });
            }
          }
        }
 else if (b.dataset.variantId) {
          // Legacy/Fallback check
          const variants = productVariantsMap[productGid];
          const variantId = b.dataset.variantId;
          const v = variants?.find((v) => v.id.endsWith(variantId || ""));
          inStock = v ? v.quantityAvailable > 0 : false;
        } else if (b.dataset.handle) {
          // Backwards compatibility for product-colors.liquid blocks
          const handle = b.dataset.handle;
          const variants = productVariantsMap[handle];
          inStock = variants ? variants.some((v) => v.quantityAvailable > 0) : false;
        }

        b.style.display = inStock ? "" : "none";
      });
    } catch (e) {
      console.error("Error updating stock badges:", e);
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.updateStockBadges();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
    this.updateStockBadges(/** @type {HTMLElement} */ (this.closest(".shopify-section") || main));
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option[selected], fieldset input:checked",
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
